import React, { useMemo, useState, useRef, useEffect } from "react";

/**
 * JotsBoard — the My Day jots day-planner.
 *
 * Backlog (persistent, scrollable) on the left → drag jots onto a fixed 9am–9pm
 * timeline on the right. Jots auto-batch by effort type:
 *   checkin (5 min) and ops (15/30 min) → one collapsed bar per type; tap to
 *     open a floating list of its jots (over the blocks below, not displacing).
 *   deep (1/2/3 hr) → one inline block each, sized to its length.
 * Blocks never overlap — a block that would collide slides below (push-down);
 * a pinned block holds its start and the auto blocks pack around it. Completed
 * jots strike through; the backend's 12am rollover deletes done jots and returns
 * the unfinished ones to the backlog.
 *
 * Mutations delegate to the parent (optimistic + persisted):
 *   onCreateJot(text, bucket, today)     onToggleJot(jot_id, nextDone)
 *   onDeleteJot(jot_id)                  onEditJotText(jot_id, text)
 *   onPromoteJot(jot, "task"|"brief")
 *   onPatchJot(jot_id, fields)           onPatchJots(jot_ids, fields)
 *     fields: { today, bucket, deep_hours, ops_minutes, pinned_start }
 *     (pinned_start: -1 clears the pin → auto-pack)
 *   onReorder(orderedJotIds)             — persist backlog ordering
 */

const START = 9 * 60;        // 9:00am
const END = 21 * 60;         // 9:00pm
const SCALE = 1.5;           // px per minute (1 hr = 90px; 15 min ≈ 22px slot)
const GAP = 0;               // blocks abut on the time axis (clean 15-min starts);
const INSET = 3;             // visual separation comes from a render inset, not time
const SNAP = 15;
const SUBLINE_MIN = 44;      // show the jot-preview subline only when the slot is ≥30 min tall
const PANEL_H = Math.min(620, Math.round((END - START) * SCALE)); // backlog scroll window (capped)
const ORDER3 = ["checkin", "ops", "deep"];

// A check-in occupies a 15-min slot; every 3 check-ins fills it, the next rolls
// the slot to the following 15 min (1–3 → 15m, 4–6 → 30m, …).
const checkinSlot = (n) => 15 * Math.max(1, Math.ceil(n / 3));

const TYPES = {
  deep:    { label: "Deep work",   glyph: "🧠", accent: "#a78bfa", tint: "rgba(167,139,250,.16)" },
  ops:     { label: "Operational", glyph: "⚙️", accent: "#2dd4bf", tint: "rgba(45,212,191,.16)" },
  checkin: { label: "Check-in",    glyph: "🔔", accent: "#f5b945", tint: "rgba(245,185,69,.16)" },
};
const typeOf = (j) => (TYPES[j?.bucket] ? j.bucket : "checkin");

const clk = (m) => { let h = Math.floor(m / 60), mm = m % 60; const ap = h >= 12 ? "pm" : "am"; h = h > 12 ? h - 12 : (h === 0 ? 12 : h); return h + (mm ? ":" + String(mm).padStart(2, "0") : "") + ap; };
const dlabel = (m) => (m >= 60 ? Math.floor(m / 60) + "h" + (m % 60 ? " " + (m % 60) + "m" : "") : m + " min");
const jmin = (j) => (j.bucket === "deep" ? (j.deep_hours || 1) * 60 : j.bucket === "ops" ? (j.ops_minutes || 15) : 5);
const blockKey = (b) => (b.type === "deep" ? b.items[0].jot_id : b.type);

export default function JotsBoard({
  jots = [],
  onCreateJot,
  onToggleJot,
  onDeleteJot,
  onPromoteJot,
  onEditJotText,
  onPatchJot,
  onPatchJots,
  onReorder,
}) {
  const [drag, setDrag] = useState(null);        // jot_id being dragged
  const [dragBlk, setDragBlk] = useState(null);  // block key being retimed
  const [openBlk, setOpenBlk] = useState(null);  // expanded batch key
  const [openTag, setOpenTag] = useState(null);  // jot_id whose type menu is open
  const [addText, setAddText] = useState("");
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const plotRef = useRef(null);
  const grabRef = useRef(0);   // px between cursor and the bar's top edge at drag start

  useEffect(() => {
    const h = (e) => {
      if (openTag && !e.target.closest?.("[data-pop]")) setOpenTag(null);
      if (openBlk && !e.target.closest?.("[data-ov]") && !e.target.closest?.("[data-blk]")) setOpenBlk(null);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [openTag, openBlk]);

  const backlog = useMemo(
    () => jots.filter((j) => j && j.jot_id && !j.today && !j.done).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [jots]
  );

  // Build + push-down-pack the day's blocks (no overlaps).
  const { laid, dayEnd, doneAll, totAll } = useMemo(() => {
    const today = jots.filter((j) => j && j.jot_id && j.today);
    let blocks = [];
    today.filter((j) => typeOf(j) === "deep").forEach((j) => blocks.push({ type: "deep", items: [j] }));
    ["ops", "checkin"].forEach((k) => {
      const items = today.filter((j) => typeOf(j) === k).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (items.length) blocks.push({ type: k, items });
    });
    blocks.forEach((b) => {
      b.key = blockKey(b);
      // ops/deep occupy the sum of their own durations; check-ins pack 3-to-a-slot.
      b.dur = b.type === "checkin" ? checkinSlot(b.items.length) : b.items.reduce((s, j) => s + jmin(j), 0);
      b.pin = b.items[0].pinned_start != null ? b.items[0].pinned_start : null;
      b.ord = Math.min(...b.items.map((j) => j.order ?? 0));
    });
    // desired start (minutes): pinned hold their pin; autos pack sequentially.
    blocks.filter((b) => b.pin != null).forEach((b) => { b.desired = b.pin; });
    const autos = blocks.filter((b) => b.pin == null).sort((a, b) => a.ord - b.ord);
    let cur = START;
    autos.forEach((b) => { b.desired = cur; cur += b.dur; });
    blocks.sort((a, b) => a.desired - b.desired);
    // visual push-down — heights (esp. collapsed batches taller than their
    // duration) never overlap; a block that would collide slides below.
    let prevBottom = 0;
    blocks.forEach((b) => {
      // Height = real duration so each block occupies exactly its slot (a 15-min
      // block is a 15-min slot). A small floor keeps the bar tappable/legible.
      b.h = Math.max(Math.round(b.dur * SCALE), b.type === "deep" ? 30 : 21);
      b.topPx = Math.max((b.desired - START) * SCALE, prevBottom);
      b.start = Math.round(START + b.topPx / SCALE);
      prevBottom = b.topPx + b.h + GAP;
    });
    let de = START, done = 0, tot = 0;
    blocks.forEach((b) => { de = Math.max(de, b.start + b.dur); b.items.forEach((j) => { if (j.done) done++; tot++; }); });
    return { laid: blocks, dayEnd: de, doneAll: done, totAll: tot };
  }, [jots]);

  const totalPx = laid.length ? Math.max(...laid.map((b) => b.topPx + b.h)) + 6 : 0;
  const plotH = Math.max(totalPx, (END - START) * SCALE) + 4;

  // ── handlers ──
  const submitAdd = () => { const t = addText.trim(); if (t) { onCreateJot?.(t, "checkin", false); setAddText(""); } };
  const commitEdit = () => { const t = editText.trim(); if (editing && t) onEditJotText?.(editing, t); setEditing(null); setEditText(""); };
  const startDragJot = (id) => (e) => { setDrag(id); setDragBlk(null); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", id); } catch {} };
  const startDragBlk = (key) => (e) => { setDragBlk(key); setDrag(null); try { grabRef.current = e.clientY - e.currentTarget.getBoundingClientRect().top; } catch { grabRef.current = 0; } e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", key); } catch {} };
  const pinBlock = (b, minutes) => onPatchJots?.(b.items.map((j) => j.jot_id), { pinned_start: minutes });
  const blockByKey = (k) => laid.find((b) => b.key === k);

  const reorderBacklog = (beforeId) => {
    if (!onReorder || !drag) return;
    const ids = backlog.map((x) => x.jot_id).filter((id) => id !== drag);
    let idx = beforeId ? ids.indexOf(beforeId) : ids.length;
    if (idx < 0) idx = ids.length;
    ids.splice(idx, 0, drag);
    onReorder(ids);
  };

  const onPlotDrop = (e) => {
    e.preventDefault();
    if (dragBlk) {
      const b = blockByKey(dragBlk);
      if (b && plotRef.current) {
        // Snap the bar's TOP edge to the slot — subtract where the user grabbed
        // it so the block lands under the cursor, not offset by the grab point.
        const y = e.clientY - plotRef.current.getBoundingClientRect().top - grabRef.current;
        const t = Math.max(START, Math.min(END - SNAP, START + Math.round((y / SCALE) / SNAP) * SNAP));
        pinBlock(b, t);
      }
      grabRef.current = 0;
      setDragBlk(null);
    } else if (drag) {
      // Land the jot at the time it was dropped — pin it to the snapped slot
      // under the cursor, instead of auto-packing it to the top of the day.
      const patch = { today: true };
      if (plotRef.current) {
        const y = e.clientY - plotRef.current.getBoundingClientRect().top;
        patch.pinned_start = Math.max(START, Math.min(END - SNAP, START + Math.round((y / SCALE) / SNAP) * SNAP));
      }
      onPatchJot?.(drag, patch);
      setDrag(null);
    }
  };

  // drop onto the backlog: a calendar block → unschedule its items; a today jot
  // → unschedule; a backlog jot → move to the end of the backlog.
  const onBacklogDrop = (e) => {
    e.preventDefault();
    if (dragBlk) {
      const b = blockByKey(dragBlk);
      if (b) onPatchJots?.(b.items.map((j) => j.jot_id), { today: false });
      setDragBlk(null);
    } else if (drag) {
      const dj = jots.find((x) => x.jot_id === drag);
      if (dj && dj.today) onPatchJot?.(drag, { today: false });
      else reorderBacklog(null);
      setDrag(null);
    }
  };

  // ── pieces ──
  const TypePill = ({ jot, onPick }) => {
    const t = TYPES[typeOf(jot)];
    return (
      <span data-pop style={{ position: "relative", flex: "0 0 auto", display: "inline-flex" }}>
        <button aria-label={`Type: ${t.label}`} onClick={(e) => { e.stopPropagation(); setOpenTag(openTag === jot.jot_id ? null : jot.jot_id); }}
          style={{ display: "inline-flex", alignItems: "center", gap: 3, border: "none", cursor: "pointer", background: t.tint, color: t.accent, borderRadius: 20, padding: "2px 8px", fontSize: 11, fontFamily: "var(--font-sans)" }}>
          <span style={{ fontSize: 11 }}>{t.glyph}</span>{t.label}<span style={{ opacity: .6, fontSize: 9 }}>▾</span>
        </button>
        {openTag === jot.jot_id && (
          <span data-pop style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 60, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 8, padding: 4, minWidth: 140, display: "flex", flexDirection: "column", gap: 2, boxShadow: "0 6px 20px rgba(0,0,0,.4)" }}>
            {ORDER3.map((k) => {
              const cur = typeOf(jot) === k;
              return (
                <button key={k} onClick={(e) => { e.stopPropagation(); onPick(k); setOpenTag(null); }}
                  style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", border: "none", cursor: "pointer", background: cur ? "var(--s2)" : "transparent", borderRadius: 6, padding: "6px 8px", fontSize: 12, color: "var(--t1)", textAlign: "left", fontFamily: "var(--font-sans)" }}>
                  <span style={{ fontSize: 12 }}>{TYPES[k].glyph}</span><span style={{ flex: 1 }}>{TYPES[k].label}</span>{cur ? "✓" : ""}
                </button>
              );
            })}
          </span>
        )}
      </span>
    );
  };

  const Toggle = ({ jot, vals, suffix, field }) => {
    const t = TYPES[typeOf(jot)];
    return (
      <span style={{ display: "inline-flex", border: "1px solid var(--bdr)", borderRadius: 14, overflow: "hidden", flex: "0 0 auto" }}>
        {vals.map((v) => {
          const on = (jot[field] || vals[0]) === v;
          return (
            <button key={v} onClick={(e) => { e.stopPropagation(); onPatchJot?.(jot.jot_id, { [field]: v }); }}
              style={{ border: "none", cursor: "pointer", padding: "2px 7px", fontSize: 10, background: on ? t.accent : "transparent", color: on ? "#0a0a0a" : "var(--t3)", fontFamily: "var(--font-mono)" }}>{v}{suffix}</button>
          );
        })}
      </span>
    );
  };

  const Check = ({ jot }) => {
    const t = TYPES[typeOf(jot)];
    return (
      <button aria-label={jot.done ? "Mark not done" : "Mark done"} onClick={(e) => { e.stopPropagation(); onToggleJot?.(jot.jot_id, !jot.done); }}
        style={{ flex: "0 0 auto", width: 16, height: 16, borderRadius: 5, border: `1.5px solid ${jot.done ? t.accent : "var(--bdr)"}`, background: jot.done ? t.accent : "transparent", color: "#0a0a0a", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, lineHeight: 1 }}>
        {jot.done ? "✓" : ""}
      </button>
    );
  };

  const ItemRow = ({ jot }) => (
    <div draggable onDragStart={startDragJot(jot.jot_id)} onDragEnd={() => setDrag(null)} onClick={(e) => e.stopPropagation()}
      style={{ display: "flex", alignItems: "center", gap: 7, background: "var(--s2)", borderRadius: 6, padding: "5px 7px", marginBottom: 4, cursor: "grab", opacity: jot.done ? 0.55 : 1 }}>
      <Check jot={jot} />
      <TypePill jot={jot} onPick={(k) => onPatchJot?.(jot.jot_id, { bucket: k })} />
      {editing === jot.jot_id ? (
        <input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)} onBlur={commitEdit}
          onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") { setEditing(null); setEditText(""); } }}
          style={{ flex: 1, fontSize: 13, background: "transparent", border: "none", borderBottom: "1px solid var(--bdr)", color: "var(--t1)", outline: "none", fontFamily: "var(--font-sans)" }} />
      ) : (
        <span onClick={(e) => { e.stopPropagation(); setEditing(jot.jot_id); setEditText(jot.text || ""); }}
          style={{ flex: 1, fontSize: 13, color: "var(--t1)", fontFamily: "var(--font-sans)", textDecoration: jot.done ? "line-through" : "none", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "text" }}>{jot.text}</span>
      )}
      {!jot.done && jot.bucket === "ops" && <Toggle jot={jot} vals={[15, 30]} suffix="m" field="ops_minutes" />}
      <button aria-label="Delete" onClick={(e) => { e.stopPropagation(); onDeleteJot?.(jot.jot_id); }}
        style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--t4)", fontSize: 12, flex: "0 0 auto" }}>✕</button>
    </div>
  );

  return (
    <div className="dm-jots-board" style={{ display: "grid", gridTemplateColumns: "minmax(0,0.66fr) minmax(0,1.34fr)", gap: 12, alignItems: "start" }}>
      {/* ── Backlog ── */}
      <div onDragOver={(e) => { if (drag || dragBlk) e.preventDefault(); }} onDrop={onBacklogDrop}
        style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: "12px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
          <span style={{ fontSize: 15 }}>🗃️</span>
          <span style={{ flex: 1, fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, color: "var(--t1)" }}>Backlog</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--t3)" }}>{backlog.length}</span>
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", margin: "0 0 10px 24px" }}>drag onto the day →</div>
        <input value={addText} onChange={(e) => setAddText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitAdd(); }} onBlur={submitAdd}
          placeholder="+ capture a jot" style={{ width: "100%", height: 32, fontSize: 13, fontFamily: "var(--font-sans)", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t1)", padding: "0 9px", outline: "none", marginBottom: 9 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: PANEL_H, overflowY: "auto", paddingRight: 2 }}>
          {backlog.map((j) => {
            const t = TYPES[typeOf(j)];
            return (
              <div key={j.jot_id} draggable onDragStart={startDragJot(j.jot_id)} onDragEnd={() => setDrag(null)}
                onDragOver={(e) => { if (drag && drag !== j.jot_id) { e.preventDefault(); e.stopPropagation(); } }}
                onDrop={(e) => {
                  if (!drag) return; e.preventDefault(); e.stopPropagation();
                  const dj = jots.find((x) => x.jot_id === drag);
                  if (dj && dj.today) onPatchJot?.(drag, { today: false }); else reorderBacklog(j.jot_id);
                  setDrag(null);
                }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--s2)", border: "1px solid var(--bdr)", borderLeft: `3px solid ${t.accent}`, borderRadius: 6, padding: "6px 8px", cursor: "grab", opacity: drag === j.jot_id ? 0.4 : 1 }}>
                <Check jot={j} />
                <TypePill jot={j} onPick={(k) => onPatchJot?.(j.jot_id, { bucket: k })} />
                {editing === j.jot_id ? (
                  <input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)} onBlur={commitEdit}
                    onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") { setEditing(null); setEditText(""); } }}
                    style={{ flex: 1, fontSize: 12, background: "transparent", border: "none", borderBottom: "1px solid var(--bdr)", color: "var(--t1)", outline: "none", fontFamily: "var(--font-sans)" }} />
                ) : (
                  <span onClick={(e) => { e.stopPropagation(); setEditing(j.jot_id); setEditText(j.text || ""); }}
                    style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--t1)", fontFamily: "var(--font-sans)", cursor: "text", lineHeight: 1.35, overflowWrap: "anywhere" }}>{j.text}</span>
                )}
                <button aria-label="Make a task" title="Make a task" onClick={(e) => { e.stopPropagation(); onPromoteJot?.(j, "task"); }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--t4)", fontSize: 11, fontFamily: "var(--font-mono)", flex: "0 0 auto" }}>→T</button>
                <button aria-label="Delete" title="Delete" onClick={(e) => { e.stopPropagation(); onDeleteJot?.(j.jot_id); }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--t4)", fontSize: 12, flex: "0 0 auto" }}>✕</button>
              </div>
            );
          })}
          {!backlog.length && <div style={{ fontSize: 12, color: "var(--t3)", padding: "8px 4px", fontFamily: "var(--font-sans)" }}>all scheduled</div>}
        </div>
      </div>

      {/* ── Today timeline ── */}
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: "12px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
          <span style={{ fontSize: 15 }}>🗓️</span>
          <span style={{ flex: 1, fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, color: "var(--t1)" }}>Today · 9a–9p</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: dayEnd > END ? "var(--r)" : "var(--t3)" }}>
            {laid.length ? (dayEnd > END ? `ends ${clk(dayEnd)} · past 9p` : `ends ${clk(dayEnd)}`) + (doneAll ? ` · ${doneAll}/${totAll} done` : "") : ""}
          </span>
        </div>
        <div ref={plotRef} onDragOver={(e) => { if (drag || dragBlk) e.preventDefault(); }} onDrop={onPlotDrop}
          style={{ position: "relative", width: "100%", height: plotH }}>
          {Array.from({ length: 13 }, (_, i) => 9 + i).map((h) => {
            const y = (h * 60 - START) * SCALE;
            return (
              <React.Fragment key={h}>
                <div style={{ position: "absolute", left: 42, right: 0, top: y, borderTop: "1px solid var(--bdr)" }} />
                <div style={{ position: "absolute", left: 0, top: y - 6, width: 38, textAlign: "right", fontSize: 10, color: "var(--t3)", fontFamily: "var(--font-mono)" }}>{clk(h * 60)}</div>
              </React.Fragment>
            );
          })}
          {!laid.length && (
            <div style={{ position: "absolute", left: 46, right: 0, top: 8, border: "1px dashed var(--bdr)", borderRadius: 8, textAlign: "center", padding: "34px 0", fontSize: 12, color: "var(--t3)", fontFamily: "var(--font-sans)" }}>drag jots from the backlog onto the day</div>
          )}
          {laid.map((b) => {
            const t = TYPES[b.type];
            const over = b.start >= END;
            if (b.type === "deep") {
              const j = b.items[0];
              return (
                <div key={b.key} draggable onDragStart={startDragBlk(b.key)} onDragEnd={() => setDragBlk(null)}
                  style={{ position: "absolute", left: 48, right: 0, top: b.topPx, height: Math.max(b.h - INSET, 24), overflow: "hidden", background: "var(--s2)", border: `1px solid ${over ? "var(--r)" : "var(--bdr)"}`, borderLeft: `3px solid ${t.accent}`, borderRadius: 8, padding: "5px 8px", boxSizing: "border-box", cursor: "grab" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "var(--t4)", fontSize: 13, flex: "0 0 auto" }}>⠿</span><Check jot={j} />
                    <span style={{ fontSize: 12 }}>{t.glyph}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--t1)", fontFamily: "var(--font-sans)", textDecoration: j.done ? "line-through" : "none", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{j.text}</span>
                    {!j.done && <Toggle jot={j} vals={[1, 2, 3]} suffix="h" field="deep_hours" />}
                    {b.pin != null && <span title="Pinned — click to auto" onClick={(e) => { e.stopPropagation(); pinBlock(b, -1); }} style={{ cursor: "pointer", color: t.accent, fontSize: 11, flex: "0 0 auto" }}>📌</span>}
                    <span style={{ fontSize: 10, color: "var(--t3)", fontFamily: "var(--font-mono)", flex: "0 0 auto" }}>{clk(b.start)}</span>
                  </div>
                </div>
              );
            }
            const done = b.items.filter((j) => j.done).length;
            const compact = b.h < SUBLINE_MIN;   // 15-min slot → single tight line
            return (
              <div key={b.key} data-blk draggable onDragStart={startDragBlk(b.key)} onDragEnd={() => setDragBlk(null)}
                onClick={(e) => { e.stopPropagation(); setOpenBlk(openBlk === b.key ? null : b.key); setOpenTag(null); }}
                style={{ position: "absolute", left: 48, right: 0, top: b.topPx, height: Math.max(b.h - INSET, 18), overflow: "hidden", background: t.tint, border: `1px solid ${over ? "var(--r)" : "transparent"}`, borderLeft: `3px solid ${t.accent}`, borderRadius: 8, padding: compact ? "2px 8px" : "6px 8px", boxSizing: "border-box", cursor: "grab", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "var(--t4)", fontSize: 13, flex: "0 0 auto" }}>⠿</span>
                  <span style={{ fontSize: 12 }}>{t.glyph}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: t.accent, fontFamily: "var(--font-sans)" }}>{t.label} · {b.items.length}</span>
                  {done > 0 && <span style={{ fontSize: 10, color: t.accent, opacity: .85, flex: "0 0 auto", fontFamily: "var(--font-mono)" }}>{done}/{b.items.length}</span>}
                  {b.pin != null && <span title="Pinned — click to auto" onClick={(e) => { e.stopPropagation(); pinBlock(b, -1); }} style={{ cursor: "pointer", color: t.accent, fontSize: 11, flex: "0 0 auto" }}>📌</span>}
                  <span style={{ fontSize: 10, color: t.accent, opacity: .85, flex: "0 0 auto", fontFamily: "var(--font-mono)" }}>{clk(b.start)} · {dlabel(b.dur)}</span>
                  <span style={{ fontSize: 12, color: t.accent, flex: "0 0 auto" }}>▾</span>
                </div>
                {!compact && <div style={{ fontSize: 10, color: t.accent, opacity: .7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 3, fontFamily: "var(--font-sans)" }}>{b.items.map((j) => j.text).join(" · ")}</div>}
              </div>
            );
          })}
          {(() => {
            const b = laid.find((x) => x.key === openBlk && x.type !== "deep");
            if (!b) return null;
            const t = TYPES[b.type];
            return (
              <div data-ov style={{ position: "absolute", left: 48, right: 0, top: b.topPx, zIndex: 50, background: "var(--s1)", border: `1px solid ${t.accent}`, borderRadius: 8, padding: "7px 9px", boxShadow: "0 10px 30px rgba(0,0,0,.5)" }}>
                <div onClick={(e) => { e.stopPropagation(); setOpenBlk(null); }} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, cursor: "pointer" }}>
                  <span style={{ fontSize: 12 }}>{t.glyph}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: t.accent, fontFamily: "var(--font-sans)" }}>{t.label} · {clk(b.start)}–{clk(b.start + b.dur)} · {dlabel(b.dur)}</span>
                  <span style={{ fontSize: 12, color: t.accent }}>▴</span>
                </div>
                {b.items.map((j) => <ItemRow key={j.jot_id} jot={j} />)}
              </div>
            );
          })()}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) { .dm-jots-board { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}
